import React, { useMemo, useState } from "react";
import { Download, Search } from "lucide-react";
import { useAdminPortal } from "../AdminPortalContext";

const AdminSubmissionsPage = () => {
  const {
    activeExportFormat,
    downloadExport,
    formatDate,
    isLoading,
    submissions,
    theme,
  } = useAdminPortal();
  const [searchTerm, setSearchTerm] = useState("");
  const isDarkTheme = theme === "dark";

  const filteredSubmissions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return submissions;
    }

    return submissions.filter((item) =>
      [
        item.name,
        item.email,
        item.company,
        item.phone,
        item.interest,
        item.message,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [searchTerm, submissions]);

  return (
    <div className="space-y-6">
      <section
        className={`rounded-[28px] border px-6 py-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold">Contact Lead Inbox</h2>
            <p
              className={`mt-2 text-sm leading-6 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
            >
              Search, review, and export all incoming contact submissions from
              one place.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div
              className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${isDarkTheme ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-slate-50"}`}
            >
              <Search
                className={`h-4 w-4 ${isDarkTheme ? "text-slate-500" : "text-slate-400"}`}
              />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search name, email, interest..."
                className={`w-full min-w-[220px] bg-transparent text-sm outline-none ${isDarkTheme ? "text-slate-100 placeholder:text-slate-500" : "text-slate-900 placeholder:text-slate-400"}`}
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => downloadExport("csv")}
                disabled={activeExportFormat === "csv"}
                className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition ${isDarkTheme ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
              >
                <Download className="h-4 w-4" />
                {activeExportFormat === "csv" ? "Exporting..." : "CSV"}
              </button>
              <button
                type="button"
                onClick={() => downloadExport("pdf")}
                disabled={activeExportFormat === "pdf"}
                className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition ${isDarkTheme ? "bg-sky-500 text-slate-950 hover:bg-sky-400" : "bg-slate-900 text-white hover:bg-slate-800"}`}
              >
                <Download className="h-4 w-4" />
                {activeExportFormat === "pdf" ? "Exporting..." : "PDF"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section
        className={`overflow-hidden rounded-[28px] border ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
      >
        <div
          className={`border-b px-6 py-5 ${isDarkTheme ? "border-slate-800" : "border-slate-200"}`}
        >
          <p
            className={`text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
          >
            {filteredSubmissions.length} result
            {filteredSubmissions.length === 1 ? "" : "s"}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead
              className={
                isDarkTheme
                  ? "bg-slate-950 text-slate-300"
                  : "bg-slate-50 text-slate-600"
              }
            >
              <tr>
                <th className="px-6 py-4 font-semibold">Date</th>
                <th className="px-6 py-4 font-semibold">Contact</th>
                <th className="px-6 py-4 font-semibold">Company</th>
                <th className="px-6 py-4 font-semibold">Interest</th>
                <th className="px-6 py-4 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    className={`px-6 py-8 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                    colSpan={5}
                  >
                    Loading submissions...
                  </td>
                </tr>
              )}

              {!isLoading && filteredSubmissions.length === 0 && (
                <tr>
                  <td
                    className={`px-6 py-8 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                    colSpan={5}
                  >
                    No submissions matched your current search.
                  </td>
                </tr>
              )}

              {!isLoading &&
                filteredSubmissions.map((item) => (
                  <tr
                    key={item.id}
                    className={
                      isDarkTheme
                        ? "border-t border-slate-800 align-top"
                        : "border-t border-slate-200 align-top"
                    }
                  >
                    <td className="px-6 py-5 whitespace-nowrap">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="px-6 py-5 min-w-[220px]">
                      <p className="font-semibold">{item.name}</p>
                      <p
                        className={`mt-1 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                      >
                        {item.email}
                      </p>
                      <p
                        className={`mt-1 ${isDarkTheme ? "text-slate-500" : "text-slate-400"}`}
                      >
                        {item.phone || "-"}
                      </p>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      {item.company || "-"}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${isDarkTheme ? "bg-slate-800 text-slate-200" : "bg-sky-50 text-sky-700"}`}
                      >
                        {item.interest}
                      </span>
                    </td>
                    <td className="px-6 py-5 min-w-[320px] leading-6">
                      {item.message}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminSubmissionsPage;
